// Export utilities — CSV, Gherkin .feature, Playwright .spec.ts, Postman collection

import * as XLSX from 'xlsx';
import type { TestCase, TcStatus } from '../types';
import type { TcFormat, ParsedGherkinCase } from './aiOutputParser';
import { scenariosToPlaywright } from './playwrightConverter';
import { scenariosToPostman } from './postmanConverter';

function csvEscape(str: string): string {
  const s = String(str ?? '').replace(/"/g, '""');
  return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s}"` : s;
}

function download(content: BlobPart, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeFilename(title: string, suffix: string, ext: string): string {
  const base = title.trim().replace(/[^a-z0-9\-_]+/gi, '_').replace(/^_+|_+$/g, '');
  return base ? `${base}_${suffix}.${ext}` : `test-cases-${suffix}_${Date.now()}.${ext}`;
}

export function exportToCsv(cases: TestCase[], format: TcFormat, title: string): void {
  if (!cases.length) return;

  let headers: string[];
  let rows: string[][];

  if (format === 'tdd') {
    headers = ['Test ID', 'Test Name', 'Requirement', 'Type', 'Input Data', 'Steps Summary', 'Expected Output', 'Pass Criteria'];
    rows = cases.map(tc => [
      tc.id,
      tc.title,
      tc.preconditions,
      tc.type,
      tc.testData,
      tc.steps.map((s, i) => `${i + 1}. ${s}`).join('\n'),
      tc.expectedResult,
      tc.passCriteria ?? '',
    ]);
  } else {
    headers = ['Test Case ID', 'Title', 'Type', 'Preconditions', 'Steps', 'Test Data', 'Expected Result'];
    rows = cases.map(tc => [
      tc.id,
      tc.title,
      tc.type,
      tc.preconditions,
      tc.steps.map((s, i) => `${i + 1}. ${s}`).join('\n'),
      tc.testData,
      tc.expectedResult,
    ]);
  }

  const csv = [headers, ...rows]
    .map(row => row.map(csvEscape).join(','))
    .join('\r\n');

  const suffix = format === 'tdd' ? 'tdd' : 'ai';
  download(csv, safeFilename(title, suffix, 'csv'), 'text/csv');
}

export function exportGherkinFeature(cases: TestCase[], featureName: string, title: string): void {
  if (!cases.length) return;
  let content = `Feature: ${featureName || 'AI Generated Test Cases'}\n\n`;
  for (const tc of cases) {
    content += `  Scenario: [${tc.id}] [${tc.type}] ${tc.title}\n`;
    for (const step of tc.steps) {
      content += `    ${step}\n`;
    }
    content += '\n';
  }
  download(content, safeFilename(title, 'bdd', 'feature'), 'text/plain');
}

export function exportPlaywrightSpec(cases: ParsedGherkinCase[], rawText: string, featureName: string, title: string): void {
  if (!cases.length) return;
  const content = scenariosToPlaywright(cases, rawText, featureName);
  if (!content) return;
  download(content, safeFilename(title, 'api', 'spec.ts'), 'text/plain');
}

export function exportPostmanCollection(cases: ParsedGherkinCase[], rawText: string, featureName: string, title: string): void {
  if (!cases.length) return;
  const collection = scenariosToPostman(cases, rawText, featureName);
  if (!collection) return;
  download(JSON.stringify(collection, null, 2), safeFilename(title, 'api', 'collection.json'), 'application/json');
}

// Downloadable output of the Web Automation page's "Generate Code with MCP" — the
// Generator/Healer agents' final, test-runner-verified Playwright spec.
export function exportMcpGeneratedSpec(code: string, title: string): void {
  if (!code) return;
  download(code, safeFilename(title, 'mcp', 'spec.ts'), 'text/plain');
}

// ── Review page exports — Excel (TDD), Gherkin, TXT ─────────────────────────
// All three export every case regardless of decision state, with Decision
// (and reviewer) surfaced as a field, per ReviewPage's "Export" dropdown.

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function dateStamp(d: Date): string {
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}

function reviewFileBase(sessionTitle: string): string {
  const base = (sessionTitle ?? '').trim().replace(/[^a-z0-9\-_]+/gi, '_').replace(/^_+|_+$/g, '');
  return base || 'Session';
}

export function decisionLabel(status: TcStatus): string {
  switch (status) {
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'pushed':    return 'Pushed';
    default:          return 'Pending';
  }
}

export function reviewerNames(tc: TestCase): string {
  const approvers = (tc.approvals ?? []).map(r => r.name || r.email);
  const rejecters = (tc.rejections ?? []).map(r => r.name || r.email);
  return [...approvers, ...rejecters].join(', ');
}

function stepsText(steps: string[] | undefined): string {
  return (steps ?? []).map((s, i) => `${i + 1}. ${s}`).join('\n');
}

// ── Excel (TDD) ──
export function buildTddWorkbook(cases: TestCase[]): XLSX.WorkBook {
  const headers = ['ID', 'Title', 'Preconditions', 'Steps', 'Test Data', 'Expected Result', 'Type', 'Decision', 'Reviewer'];
  const rows = cases.map(tc => [
    tc.id ?? '',
    tc.title ?? '',
    tc.preconditions ?? '',
    stepsText(tc.steps),
    tc.testData ?? '',
    tc.expectedResult ?? '',
    tc.type ?? '',
    decisionLabel(tc.status),
    reviewerNames(tc),
  ]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Test Cases');
  return wb;
}

export function exportReviewExcel(cases: TestCase[], sessionTitle: string, now: Date = new Date()): void {
  if (!cases.length) return;
  const wb = XLSX.write(buildTddWorkbook(cases), { type: 'array', bookType: 'xlsx' });
  download(wb, `${reviewFileBase(sessionTitle)}_TDD_${dateStamp(now)}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

// ── Gherkin ── Preconditions -> Given, each step -> When/And, Expected Result -> Then.
export function buildReviewGherkin(cases: TestCase[], sessionTitle: string): string {
  let content = `Feature: ${sessionTitle || 'Test Cases'}\n\n`;
  for (const tc of cases) {
    content += `  Scenario: [${tc.id}] [${tc.type}] ${tc.title}\n`;
    content += `    # Decision: ${decisionLabel(tc.status)}\n`;
    if (tc.preconditions) content += `    Given ${tc.preconditions}\n`;
    (tc.steps ?? []).forEach((step, i) => {
      content += `    ${i === 0 ? 'When' : 'And'} ${step}\n`;
    });
    if (tc.expectedResult) content += `    Then ${tc.expectedResult}\n`;
    content += '\n';
  }
  return content;
}

export function exportReviewGherkin(cases: TestCase[], sessionTitle: string, now: Date = new Date()): void {
  if (!cases.length) return;
  const content = buildReviewGherkin(cases, sessionTitle);
  download(content, `${reviewFileBase(sessionTitle)}_${dateStamp(now)}.feature`, 'text/plain');
}

// ── TXT ──
export function buildReviewTxt(cases: TestCase[]): string {
  return cases.map(tc => [
    `ID: ${tc.id}`,
    `Type: ${tc.type}`,
    `Decision: ${decisionLabel(tc.status)}`,
    `Preconditions: ${tc.preconditions ?? ''}`,
    `Steps:`,
    ...((tc.steps ?? []).length ? tc.steps.map((s, i) => `  ${i + 1}. ${s}`) : ['  (none)']),
    `Expected Result: ${tc.expectedResult ?? ''}`,
  ].join('\n')).join(`\n\n${'-'.repeat(40)}\n\n`);
}

export function exportReviewTxt(cases: TestCase[], sessionTitle: string, now: Date = new Date()): void {
  if (!cases.length) return;
  const content = buildReviewTxt(cases);
  download(content, `${reviewFileBase(sessionTitle)}_${dateStamp(now)}.txt`, 'text/plain');
}
